import Phaser from 'phaser';
import { BaseGameScene } from './BaseGameScene';
import { HEADER_HEIGHT } from '../config';
import { COLORS, DISPLAY_FONT, Radii } from '../ui/theme';
import { store } from '../state/store';
import { baking } from '../systems/BakingSystem';
import { ovenTimers } from '../systems/OvenTimers';
import { Button } from '../ui/Button';
import type { Bake, Doneness, Recipe, SceneName } from '../types';

// Prototype-faithful oven:
// - Control strip overlays the top of the scene: TIMER 1 | OVEN TEMP | TIMER 2.
//   Timers are pure stopwatches (✓ start/stop, ✗ reset), always available.
// - The oven art's own baked-in control strip is cropped off via a texture
//   frame so the live strip is the only one, and both door states render in
//   the same footprint (bottom-anchored, common height).
// - BAKE IT hands the pan over as `carriedBake`: a pulsing mitts button in the
//   top right. Click it to take the pan in hand (door opens), then click a
//   rack target to set it down — cooking starts at placement. Click a placed
//   pan to take it out. Hovering a rack shows the target brackets + a hint.
const DEEP = 0x4a3670;
const PANEL_TEXT = '#efe3b8';
const TARGET_YELLOW = 0xf0d020;

const DONENESS_TINT: Record<Doneness, number | null> = {
  raw: null,
  baking: 0xffd9a8,
  perfect: 0xd9a066,
  burnt: 0x5a4030,
};

// The Scratch oven art comes on padded canvases: each texture also contains a
// baked-in control strip (replaced by our live one) and, on the open art, a
// detached door-leaf sliver on the far right. These content rects (fractions
// of the source canvas) isolate just the oven body. Measured from the art:
// both bodies are 327 source-units wide — the same oven at the same scale —
// so equal display scale keeps the footprint identical across door states.
const CONTENT: Record<string, { x: number; y: number; w: number; h: number }> = {
  'oven-closed':     { x: 0, y: 0.23, w: 1.00, h: 0.77 },
  'oven-open-empty': { x: 0, y: 0.38, w: 0.66, h: 0.59 },
};

// Rack geometry as fractions of the cropped body's display rect.
const RACKS: { rack: 1 | 2; label: string; lineY: number }[] = [
  { rack: 1, label: 'Top Rack', lineY: 0.50 },
  { rack: 2, label: 'Bottom Rack', lineY: 0.85 },
];
const ZONE_W = 0.66;
const ZONE_H = 0.24;

export class OvenScene extends BaseGameScene {
  private ovenTemp = 350;
  private doorOpen = false;
  private inHands = false;
  // Rack zones sit above the oven body; both receive the same pointerup.
  // Marking the event's timestamp (not a boolean) means a mid-click scene
  // rebuild can't strand a stale "consumed" flag and eat the next click.
  private consumedUpTime = -1;

  private tempText!: Phaser.GameObjects.Text;
  private timerTexts: [Phaser.GameObjects.Text?, Phaser.GameObjects.Text?] = [];
  private checkButtons: [Phaser.GameObjects.Image?, Phaser.GameObjects.Image?] = [];
  private infoText!: Phaser.GameObjects.Text;

  private ovenNodes: Phaser.GameObjects.GameObject[] = [];
  private panImages: Map<string, Phaser.GameObjects.Image> = new Map();
  private hoverG!: Phaser.GameObjects.Graphics;
  private hoverLabel!: Phaser.GameObjects.Container;
  private ghostPan: Phaser.GameObjects.Image | null = null;

  constructor() {
    super({ key: 'Oven' });
  }

  protected sceneName(): Extract<SceneName, 'Menu' | 'Kitchen' | 'Oven' | 'Counter'> { return 'Oven'; }
  protected title(): string { return ''; }
  protected backgroundColor(): number { return COLORS.bgOven; }

  create(): void {
    this.setupChrome();
    this.doorOpen = false;
    this.inHands = false;

    for (const bake of store.state.activeBakes) baking.ensureRuntime(bake, this.ovenTemp);

    this.buildControlStrip();
    this.renderOven();
    this.buildCarriedButton();

    // Carried pan rides the cursor while in hands.
    this.input.on(Phaser.Input.Events.POINTER_MOVE, (p: Phaser.Input.Pointer) => {
      this.ghostPan?.setPosition(p.worldX, p.worldY);
    });

    this.events.on(Phaser.Scenes.Events.UPDATE, () => this.refreshDisplays());
  }

  // ── Control strip (always on top) ─────────────────────────────────────────

  private buildControlStrip(): void {
    const w = this.scale.width;
    const stripY = HEADER_HEIGHT + 20;
    const stripH = 140;

    const tray = this.add.graphics().setDepth(10);
    tray.fillStyle(0xcfc3e8, 0.85);
    tray.fillRoundedRect(w / 2 - 560, stripY - 8, 1120, stripH + 16, Radii.xl);

    const panelW = 330;
    const centers: [number, number, number] = [w / 2 - 380, w / 2, w / 2 + 380];

    // TIMER panels — pure stopwatches, always active.
    ([0, 1] as const).forEach((idx) => {
      const cx = centers[idx === 0 ? 0 : 2];
      this.panelDeep(cx - panelW / 2, stripY, panelW, stripH);
      this.add.text(cx - 24, stripY + 26, `TIMER ${idx + 1}`, {
        fontFamily: DISPLAY_FONT, fontSize: '22px', fontStyle: '600', color: PANEL_TEXT,
      }).setOrigin(0.5).setDepth(11);
      this.timerTexts[idx] = this.add.text(cx - 24, stripY + 78, '0:00:00', {
        fontFamily: '"VT323", monospace', fontSize: '52px', color: PANEL_TEXT,
      }).setOrigin(0.5).setDepth(11);

      // ✓ start/stop toggle
      const checkKey = `timer${idx + 1}-start-check-off`;
      if (this.textures.exists(checkKey)) {
        const check = this.add.image(cx + 108, stripY + 56, checkKey).setDepth(11);
        check.setScale(46 / check.height);
        check.setInteractive({ useHandCursor: true });
        check.on('pointerup', () => ovenTimers.toggle(idx));
        this.checkButtons[idx] = check;
      }
      // ✗ reset
      const xKey = `timer${idx + 1}-reset-x`;
      if (this.textures.exists(xKey)) {
        const xBtn = this.add.image(cx + 108, stripY + 104, xKey).setDepth(11);
        xBtn.setScale(46 / xBtn.height);
        xBtn.setInteractive({ useHandCursor: true });
        xBtn.on('pointerup', () => ovenTimers.reset(idx));
      }
    });

    // OVEN TEMP panel (center)
    const cx = centers[1];
    this.panelDeep(cx - panelW / 2, stripY, panelW, stripH);
    this.add.text(cx, stripY + 26, 'OVEN TEMP', {
      fontFamily: DISPLAY_FONT, fontSize: '22px', fontStyle: '600', color: PANEL_TEXT,
    }).setOrigin(0.5).setDepth(11);

    const badge = this.add.graphics().setDepth(11);
    badge.lineStyle(3, 0xf0c040, 1);
    badge.strokeRoundedRect(cx - 130, stripY + 58, 88, 40, 12);
    this.add.text(cx - 86, stripY + 78, 'BAKE', {
      fontFamily: DISPLAY_FONT, fontSize: '20px', fontStyle: '600', color: PANEL_TEXT,
    }).setOrigin(0.5).setDepth(11);

    this.tempText = this.add.text(cx + 4, stripY + 78, String(this.ovenTemp), {
      fontFamily: '"VT323", monospace', fontSize: '52px', color: PANEL_TEXT,
    }).setOrigin(0.5).setDepth(11);

    this.circleButton(cx + 82, stripY + 78, '+', () => this.bumpTemp(+25));
    this.circleButton(cx + 122, stripY + 78, '−', () => this.bumpTemp(-25));

    // What's cooking summary line beneath the strip.
    this.infoText = this.add.text(w / 2, stripY + stripH + 22, '', {
      fontFamily: DISPLAY_FONT, fontSize: '15px', fontStyle: 'italic', color: '#8a7ab5',
    }).setOrigin(0.5).setDepth(11);
  }

  private panelDeep(x: number, y: number, w: number, h: number): void {
    const g = this.add.graphics().setDepth(10);
    g.fillStyle(DEEP, 1);
    g.fillRoundedRect(x, y, w, h, Radii.md);
    g.lineStyle(2, 0x2e2050, 1);
    g.strokeRoundedRect(x, y, w, h, Radii.md);
  }

  private circleButton(x: number, y: number, label: string, onClick: () => void): void {
    const c = this.add.container(x, y).setDepth(11);
    const g = this.add.graphics();
    g.fillStyle(0x8f7fc0, 1);
    g.fillCircle(0, 0, 16);
    g.lineStyle(2, 0xefe3b8, 1);
    g.strokeCircle(0, 0, 16);
    c.add(g);
    c.add(this.add.text(0, 0, label, {
      fontFamily: DISPLAY_FONT, fontSize: '22px', fontStyle: '700', color: PANEL_TEXT,
    }).setOrigin(0.5));
    c.setInteractive(new Phaser.Geom.Rectangle(-18, -18, 36, 36), Phaser.Geom.Rectangle.Contains);
    c.input!.cursor = 'pointer';
    c.on('pointerover', () => c.setScale(1.12));
    c.on('pointerout', () => c.setScale(1));
    c.on('pointerup', onClick);
  }

  // ── Oven body (same footprint for both door states) ──────────────────────

  private ensureBodyFrame(key: string): void {
    const tex = this.textures.get(key);
    if (tex.has('body')) return;
    // Fractions apply to the RASTER (base frame) dimensions — getSourceImage
    // reports the SVG's natural size, which is smaller than the loaded
    // texture (load.svg scale multiplier) and would crop the wrong region.
    const base = tex.get('__BASE');
    const c = CONTENT[key] ?? { x: 0, y: 0, w: 1, h: 1 };
    tex.add(
      'body', 0,
      Math.round(base.width * c.x), Math.round(base.height * c.y),
      Math.round(base.width * c.w), Math.round(base.height * c.h),
    );
  }

  private bodyRect(): { x: number; y: number; w: number; h: number } {
    const w = this.scale.width;
    const h = this.scale.height;
    const top = HEADER_HEIGHT + 200;
    const bottom = h - 24;
    return { x: w / 2, y: bottom, w: w * 0.92, h: bottom - top };
  }

  private renderOven(): void {
    this.ovenNodes.forEach((n) => n.destroy());
    this.ovenNodes = [];
    this.panImages.clear();
    this.ghostPan = null;
    const track = <T extends Phaser.GameObjects.GameObject>(n: T): T => {
      this.ovenNodes.push(n);
      return n;
    };

    const bodyKey = this.doorOpen ? 'oven-open-empty' : 'oven-closed';
    const rect = this.bodyRect();
    let body: Phaser.GameObjects.Image | null = null;
    if (this.textures.exists(bodyKey)) {
      // One display scale for BOTH door states: the two body frames come from
      // the same source art at the same unit scale, so a shared multiplier
      // keeps the oven pixel-stable when the door toggles.
      this.ensureBodyFrame('oven-closed');
      this.ensureBodyFrame('oven-open-empty');
      const commonScale = (['oven-closed', 'oven-open-empty'] as const)
        .filter((k) => this.textures.exists(k))
        .map((k) => {
          const f = this.textures.getFrame(k, 'body');
          return Math.min(rect.w / f.width, rect.h / f.height);
        })
        .reduce((a, b) => Math.min(a, b), Infinity);

      body = track(this.add.image(rect.x, rect.y, bodyKey, 'body'));
      body.setOrigin(0.5, 1);
      body.setScale(commonScale);
      body.setDepth(1);
      body.setInteractive({ useHandCursor: true });
      body.on('pointerup', (p: Phaser.Input.Pointer) => {
        if (p.upTime === this.consumedUpTime) return;
        this.doorOpen = !this.doorOpen;
        this.renderOven();
      });
    }

    track(this.add.text(rect.x, rect.y + 12,
      this.doorOpen ? 'click the oven to close the door' : 'click the oven to open the door', {
        fontFamily: DISPLAY_FONT, fontSize: '14px', fontStyle: 'italic', color: '#9a8cc0',
      }).setOrigin(0.5, 0).setDepth(2));

    // Hover target overlay (brackets + label), reused across zones.
    this.hoverG = track(this.add.graphics().setDepth(6));
    this.hoverLabel = track(this.add.container(0, 0).setDepth(7));
    this.hoverLabel.setVisible(false);

    if (this.doorOpen && body) {
      const bw = body.displayWidth;
      const bh = body.displayHeight;
      const bodyTop = rect.y - bh;

      for (const rk of RACKS) {
        const bake = store.state.activeBakes.find((b) => b.rack === rk.rack) ?? null;
        const baselineY = bodyTop + bh * rk.lineY;
        const zone = track(this.add.zone(rect.x, baselineY - (bh * ZONE_H) / 2, bw * ZONE_W, bh * ZONE_H));
        (zone as Phaser.GameObjects.Zone).setInteractive({ useHandCursor: true });
        zone.setDepth(5);

        // Placed pan on this rack
        if (bake) {
          const pan = track(this.add.image(rect.x, baselineY + 8, this.panKeyFor(store.recipeById(bake.recipe))));
          pan.setOrigin(0.5, 1);
          pan.setScale((bw * 0.40) / pan.width);
          pan.setDepth(4);
          this.panImages.set(bake.id, pan);
        }

        zone.on('pointerover', () => this.showTarget(rect.x, baselineY, bw, bh, rk, bake));
        zone.on('pointerout', () => this.hideTarget());
        zone.on('pointerup', (p: Phaser.Input.Pointer) => {
          if (!bake && !this.inHands) return; // empty rack, empty hands — let the door toggle
          this.consumedUpTime = p.upTime;
          if (bake) {
            this.takeOut(bake.id);
          } else if (this.inHands) {
            this.place(rk.rack);
          }
        });
      }

      // Pan in hands: the mitts follow the cursor until placed (pointermove
      // handler registered in create).
      if (this.inHands && store.state.carriedBake) {
        const key = this.loaderKeyFor(store.recipeById(store.state.carriedBake.recipe));
        if (this.textures.exists(key)) {
          const p = this.input.activePointer;
          const startX = p.worldX || rect.x;
          const startY = p.worldY || bodyTop + bh * 0.22;
          this.ghostPan = track(this.add.image(startX, startY, key));
          this.ghostPan.setScale((bw * 0.42) / this.ghostPan.width);
          this.ghostPan.setDepth(8);
          this.ghostPan.setAlpha(0.95);
        }
      }

      this.applyDonenessTints();
    }
  }

  private showTarget(cx: number, baselineY: number, bw: number, bh: number, rk: { rack: 1 | 2; label: string }, bake: Bake | null): void {
    const zw = bw * ZONE_W;
    const zh = bh * ZONE_H;
    const top = baselineY - zh;
    const g = this.hoverG;
    g.clear();

    let hint: string | null = null;
    if (bake) hint = 'click to remove pan';
    else if (this.inHands) hint = 'click to place pan';
    if (!hint) return; // empty rack, nothing in hands — no affordance

    // Corner brackets
    g.lineStyle(6, TARGET_YELLOW, 0.95);
    const bl = Math.min(zw, zh) * 0.28;
    const corners: [number, number, number, number][] = [
      [cx - zw / 2, top, 1, 1], [cx + zw / 2, top, -1, 1],
      [cx - zw / 2, top + zh, 1, -1], [cx + zw / 2, top + zh, -1, -1],
    ];
    for (const [px, py, dx, dy] of corners) {
      g.beginPath();
      g.moveTo(px + dx * bl, py);
      g.lineTo(px, py);
      g.lineTo(px, py + dy * bl);
      g.strokePath();
    }

    // Label bar
    this.hoverLabel.removeAll(true);
    const labelBg = this.add.graphics();
    labelBg.fillStyle(TARGET_YELLOW, 0.85);
    labelBg.fillRoundedRect(-120, -42, 240, 44, 8);
    labelBg.fillStyle(0xfff8d0, 0.9);
    labelBg.fillRoundedRect(-140, 4, 280, 26, 6);
    this.hoverLabel.add(labelBg);
    this.hoverLabel.add(this.add.text(0, -20, rk.label, {
      fontFamily: DISPLAY_FONT, fontSize: '26px', fontStyle: '600', color: '#6a5a10',
    }).setOrigin(0.5));
    this.hoverLabel.add(this.add.text(0, 17, hint, {
      fontFamily: DISPLAY_FONT, fontSize: '16px', color: '#7a6a20',
    }).setOrigin(0.5));
    this.hoverLabel.setPosition(cx, top - 8);
    this.hoverLabel.setVisible(true);
  }

  private hideTarget(): void {
    this.hoverG.clear();
    this.hoverLabel.setVisible(false);
  }

  // ── Carried pan button (top right) ────────────────────────────────────────

  private carriedBtn: Phaser.GameObjects.Container | null = null;

  private buildCarriedButton(): void {
    this.carriedBtn?.destroy();
    this.carriedBtn = null;
    const carried = store.state.carriedBake;
    if (!carried || this.inHands) return;

    // Below the control strip, right side — clear of the TIMER 2 panel.
    const w = this.scale.width;
    const key = this.loaderKeyFor(store.recipeById(carried.recipe));
    const c = this.add.container(w - 125, HEADER_HEIGHT + 245).setDepth(12);
    if (this.textures.exists(key)) {
      const img = this.add.image(0, 0, key);
      img.setScale(190 / img.width);
      c.add(img);
    }
    c.add(this.add.text(0, 58, 'pan ready!', {
      fontFamily: DISPLAY_FONT, fontSize: '16px', fontStyle: '600', color: '#c2571f',
    }).setOrigin(0.5));

    c.setInteractive(new Phaser.Geom.Rectangle(-100, -55, 200, 130), Phaser.Geom.Rectangle.Contains);
    c.input!.cursor = 'pointer';
    c.on('pointerup', () => {
      this.inHands = true;
      this.doorOpen = true;
      this.carriedBtn?.destroy();
      this.carriedBtn = null;
      this.renderOven();
    });

    this.tweens.add({
      targets: c,
      scale: { from: 1, to: 1.12 },
      duration: 650,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.carriedBtn = c;
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  private place(rack: 1 | 2): void {
    const bake = store.placePan(rack);
    if (!bake) return;
    // Cooking starts the moment the pan lands on the rack, and the rack's
    // countdown timer auto-sets to the recipe's cook time.
    baking.ensureRuntime(bake, this.ovenTemp);
    baking.start(bake.id);
    ovenTimers.setFor((rack - 1) as 0 | 1, bake.durationSec);
    this.inHands = false;
    this.hideTarget();
    this.renderOven();
  }

  // Taking a pan out locks its doneness — ask first.
  private takeOut(bakeId: string): void {
    const bake = store.state.activeBakes.find((b) => b.id === bakeId);
    if (!bake) return;
    const recipe = store.recipeById(bake.recipe);
    this.hideTarget();
    this.showConfirm(
      `Take the ${recipe?.name ?? 'pan'} out of the oven?`,
      'Take It Out!',
      'Not Yet',
      () => this.doTakeOut(bakeId),
    );
  }

  private doTakeOut(bakeId: string): void {
    const bake = store.state.activeBakes.find((b) => b.id === bakeId);
    const rack = bake?.rack;
    const batch = store.takeOut(bakeId);
    if (!batch) return;
    baking.forget(bakeId);
    if (rack) ovenTimers.clear((rack - 1) as 0 | 1);
    this.renderOven();
    this.buildCarriedButton();
  }

  // ── Confirmation modal ────────────────────────────────────────────────────

  private confirmNodes: Phaser.GameObjects.GameObject[] = [];

  private showConfirm(question: string, yesLabel: string, noLabel: string, onYes: () => void): void {
    this.closeConfirm();
    const w = this.scale.width;
    const h = this.scale.height;
    const track = <T extends Phaser.GameObjects.GameObject>(n: T): T => {
      this.confirmNodes.push(n);
      return n;
    };

    // Full-screen dimmer that swallows every click behind the dialog.
    const dim = track(this.add.rectangle(0, 0, w, h, 0x2a1f4a, 0.55).setOrigin(0, 0).setDepth(40));
    dim.setInteractive();

    const pw = 560;
    const ph = 210;
    const px = w / 2 - pw / 2;
    const py = h / 2 - ph / 2;
    const g = track(this.add.graphics().setDepth(41));
    g.fillStyle(0xfffbe6, 1);
    g.fillRoundedRect(px, py, pw, ph, Radii.lg);
    g.lineStyle(4, COLORS.headerPurple, 1);
    g.strokeRoundedRect(px, py, pw, ph, Radii.lg);

    track(this.add.text(w / 2, py + 60, question, {
      fontFamily: DISPLAY_FONT, fontSize: '26px', fontStyle: '600', color: '#553089',
      align: 'center', wordWrap: { width: pw - 60 },
    }).setOrigin(0.5).setDepth(42));

    track(new Button(this, w / 2 - 120, py + ph - 55, yesLabel, {
      fill: COLORS.accentGreen, hoverFill: 0x5fe28b, fontSize: '20px', width: 190, height: 50,
      onClick: () => { this.closeConfirm(); onYes(); },
    })).setDepth(42);
    track(new Button(this, w / 2 + 120, py + ph - 55, noLabel, {
      fill: 0xaaaaaa, hoverFill: 0xcccccc, fontSize: '20px', width: 160, height: 50,
      onClick: () => this.closeConfirm(),
    })).setDepth(42);
  }

  private closeConfirm(): void {
    this.confirmNodes.forEach((n) => n.destroy());
    this.confirmNodes = [];
  }

  private bumpTemp(delta: number): void {
    this.ovenTemp = Phaser.Math.Clamp(this.ovenTemp + delta, 200, 500);
    this.tempText.setText(String(this.ovenTemp));
    baking.setOvenTemp(this.ovenTemp);
  }

  private panKeyFor(recipe: Recipe | undefined): string {
    const isCup = recipe?.itemType === 'cupcake' || recipe?.itemType === 'muffin';
    return isCup
      ? 'oven-pan-top-oven-pan-cupcake-unbaked'
      : 'oven-pan-top-oven-pan-sheet-cookies-unbaked';
  }

  private loaderKeyFor(recipe: Recipe | undefined): string {
    const isCup = recipe?.itemType === 'cupcake' || recipe?.itemType === 'muffin';
    return isCup ? 'loader-cupcake-pan-with-hands' : 'loader-sheet-pan-w-hands';
  }

  private applyDonenessTints(): void {
    for (const [bakeId, pan] of this.panImages.entries()) {
      const bake = store.state.activeBakes.find((b) => b.id === bakeId);
      if (!bake) continue;
      const tint = DONENESS_TINT[bake.doneness];
      if (tint === null) pan.clearTint();
      else pan.setTint(tint);
    }
  }

  private refreshDisplays(): void {
    ([0, 1] as const).forEach((idx) => {
      const timer = ovenTimers.timers[idx];
      const t = this.timerTexts[idx];
      if (t) {
        t.setText(formatElapsed(timer.remainingSec));
        // Time's up: the digits turn red until the pan comes out.
        t.setColor(timer.seedSec > 0 && timer.remainingSec <= 0 ? '#ff8f8f' : PANEL_TEXT);
      }
      const check = this.checkButtons[idx];
      if (check) {
        const wanted = timer.running
          ? `timer${idx + 1}-start-check-on`
          : `timer${idx + 1}-start-check-off`;
        if (check.texture.key !== wanted && this.textures.exists(wanted)) check.setTexture(wanted);
      }
    });

    const parts: string[] = [];
    for (const b of store.state.activeBakes) {
      const r = store.recipeById(b.recipe);
      parts.push(`Rack ${b.rack}: ${r?.name ?? b.recipe} (${r?.tempF}°F · ${r?.minutes} min)`);
    }
    if (store.state.carriedBake) {
      const r = store.recipeById(store.state.carriedBake.recipe);
      parts.push(`In hand: ${r?.name ?? '?'}`);
    }
    this.infoText.setText(parts.join('    •    '));

    this.applyDonenessTints();
  }
}

function formatElapsed(sec: number): string {
  const s = Math.floor(sec);
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return `${hh}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}
